import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";

import useStateHandler from '@/hooks/useStateHandler';
import LikeButton from '@/shared-components/buttons/components/LikeButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import ArtistVerifiedIcon from '@/shared-components/icons/components/user-profile/ArtistVerifiedIcon';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import './styles/ThoughtCard.css';

export default function ThoughtCard({ thoughtData, loadRepliesOfThought, setReply, onLikeAction, deleteThought }) {
  const [repliesLoading, setRepliesLoading] = useState(false);
  const { state } = useStateHandler(); // Retrieve user session
  const navigate = useNavigate();
  
  const thoughtTextWithMentions = useMemo(
    () => Utils.parserUserMentions(thoughtData.thought_message),
    [thoughtData.thought_message]
  );
  const isReply = Boolean(thoughtData.thought_parent);
  const isLoading = Boolean(thoughtData.isLoading);
  const isSpecial = Boolean(thoughtData.isSpecial);

  function navigateToUserProfile() {
    navigate(`/user/${thoughtData.thought_creator.user_username}`, {
      state: { from: true }
    });
  }

  // Debug logs for verification.
  const currentUser = state?.user_session?.user_username;
  const creatorUser = thoughtData.thought_creator.user_username;
  const createdAtDate = new Date(thoughtData.createdAt);
  const nowDate = new Date();
  const diffMs = nowDate - createdAtDate;

  // Calculate if the current user can delete the thought (within 5 minutes)
  const canDelete = currentUser === creatorUser && diffMs <= (5 * 60 * 1000);

  const handleDelete = (e) => {
    e.stopPropagation();
    deleteThought(thoughtData._id);
  };

  if (!thoughtData.thought_creator) 
  {
    return <span style={{ width: '100px', height: '100px' }}></span>;
  }

  return (
    <div
      className={`artwork-view-thought__container element-non-selectable ${
        isReply ? 'reply' : ''
      } ${isLoading ? 'read-only' : ''} ${isSpecial ? 'special' : ''}`}
    >
      <UserProfileImage
        typeOfProfile={thoughtData.thought_creator.user_type}
        className="artwork-view-thought__user-image"
        image={thoughtData.thought_creator.user_image?.image_id}
        onClick={navigateToUserProfile}
      />
      <div className="artwork-view-thought__text-section">
        <Text
          className="artwork-view-thought__small-text username dots-on-overflow font-size-medium"
          onClick={navigateToUserProfile}>
          {thoughtData.thought_creator.user_username}
          {thoughtData.thought_creator.user_type === 'artist' &&
            thoughtData.thought_creator.user_flags.suarteroad_completed && (
            <ArtistVerifiedIcon className="artwork-view-thought__artist-verified-icon" />
          )}
        </Text>
  
        <Text className="artwork-view-thought__text">
          {thoughtTextWithMentions}
        </Text>          

        {isLoading ? (
          <Text className="artwork-view-thought__small-text loading font-size-small">
            Publishing...
            <Loader className="artwork-view-thought__spinner" thin />
          </Text>
        ) : (
          <>
            <div>
              <Text className="artwork-view-thought__small-text elapsed font-size-small">
                {Utils.elapsedTime(new Date(thoughtData.createdAt).getTime())}
              </Text>
              <Text
                className="artwork-view-thought__small-text reply font-size-small"
                onClick={() => setReply(thoughtData)}>
                Reply
              </Text>
            </div>

            {Boolean(thoughtData.hasReplies) &&
              (repliesLoading ? (
                <Text className="artwork-view-thought__small-text view-more font-size-small">
                  Loading replies...
                </Text>
              ) : (

                <Text
                  className="artwork-view-thought__small-text view-more font-size-small"
                  onClick={() => {
                    setRepliesLoading(true);
                    loadRepliesOfThought(thoughtData);
                  }}>
                  {isReply
                    ? `View more (${thoughtData.hasReplies.repliesLeft})`
                    : `View replies (${thoughtData.hasReplies.repliesLeft})`}
                </Text>
              ))}
          </>
        )}

        {!isLoading && (
          <div className="artwork-view-thought__like-section">
            <div className="artwork-view-thought__like-actions">
              {canDelete && (
                <DeleteIcon 
                  onClick={handleDelete} 
                  className="artwork-view-thought__delete-button"
                />
              )}
              <LikeButton 
                itemId={thoughtData._id}
                typeOfItem="THOUGHT"
                isLiked={thoughtData.is_liked} 
                className="artwork-view-thought__like" 
                onLikeAction={likeStatus => onLikeAction(likeStatus, thoughtData._id)}
              />
            </div>
            <span className="artwork-view-thought__like-count st-m">
              {thoughtData?.like_count > 0 &&
                  Utils.numberParserMillionThousand(thoughtData.like_count)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
